import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { TextArea } from "@/components/ui/input"
import { TimeWindowFormSchema, timeWindowTextMaxLength } from "@/modules/patients/agenda/time-window/time-window-schema"
import { PhoneNumberInput } from "@/modules/patients/components/phoneNumber/PhoneNumberInput"

interface AlternativeTriageFieldsetProps {
  isViewMode: boolean
  isEditMode: boolean
}

export const AlternativeTriageFieldset = ({ isViewMode, isEditMode }: AlternativeTriageFieldsetProps) => {
  const { t } = useTranslation()
  const { control, getValues } = useFormContext<TimeWindowFormSchema>()

  const watchedAlternativeTriage = useWatch({ control, name: "alternativeTriage" })
  const watchedReferredBy = useWatch({ control, name: "referredBy" })

  const showAlternativeTriage =
    (getValues("alternativeTriage") || watchedAlternativeTriage) &&
    (getValues("referredBy") === "MEDGATE" || watchedReferredBy === "MEDGATE")

  if (!showAlternativeTriage) return null

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-on-surface mb-3 text-base font-medium" data-testid="alternative-triage-legend">
        {t("patient.agenda.timeWindowForm.alternativeTriage")}
      </legend>

      <div className="grid gap-4">
        <Controller
          name="alternativeTriagePhoneNumber"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <PhoneNumberInput
              data-testid="alternative-triage-phone-number"
              value={value}
              onChange={onChange}
              label={`${t("patient.agenda.timeWindowForm.alternativeTriagePhoneNumber")}*`}
              error={t(error?.message || "")}
              disabled={isViewMode || isEditMode}
              isCorrespondence
            />
          )}
        />
        <Controller
          name="alternativeTriageReason"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextArea
              {...field}
              value={field.value ?? ""}
              data-testid="alternative-triage-reason"
              label={t("patient.agenda.timeWindowForm.alternativeTriageReason")}
              error={t(error?.message || "")}
              disabled={isViewMode || isEditMode}
              textLimit={timeWindowTextMaxLength}
            />
          )}
        />
      </div>
    </fieldset>
  )
}
